import { ImagePlus, Pencil, Trash2 } from 'lucide-react'
import * as React from 'react'
import { useCallback, useState } from 'react'
import { useI18n } from '../../../i18n'
import { cn } from '../utils'
import { Button } from './button'

export interface ImageUploadProps {
  value?: string
  onChange: (url: string | null) => void
  label?: string
  className?: string
}

export function ImageUpload({ value, onChange, label, className }: ImageUploadProps) {
  const { t } = useI18n()
  const fieldLabel = label ?? t('imageUpload.label')
  const [isDragging, setIsDragging] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const inputId = React.useId()
  const errorId = `${inputId}-error`

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return

      const file = files[0]
      if (!file || !file.type.startsWith('image/')) {
        setUploadError(t('imageUpload.invalidFile'))
        if (inputRef.current) inputRef.current.value = ''
        return
      }

      setUploadError(null)

      const reader = new FileReader()
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          onChange(reader.result)
        }
      }
      reader.readAsDataURL(file)
    },
    [onChange, t]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)
      handleFiles(e.dataTransfer.files)
    },
    [handleFiles]
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback(() => {
    setIsDragging(false)
  }, [])

  return (
    <div className={cn('space-y-2', className)}>
      {fieldLabel && (
        <label htmlFor={inputId} className="text-sm font-medium text-gray-700">
          {fieldLabel}
        </label>
      )}

      <input
        id={inputId}
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={(e) => handleFiles(e.target.files)}
        aria-describedby={uploadError ? errorId : undefined}
        className="sr-only"
      />

      {value ? (
        <div className="relative group">
          <div className="w-32 h-32 rounded-lg overflow-hidden border-2 border-gray-200">
            <img
              src={value}
              alt={t('imageUpload.previewAlt', { label: fieldLabel })}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/50 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity rounded-lg">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => inputRef.current?.click()}
              className="p-2 bg-white rounded-full text-gray-700 hover:bg-gray-100"
              aria-label={t('imageUpload.changePhoto')}
            >
              <Pencil className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onChange(null)}
              className="p-2 bg-white rounded-full text-red-600 hover:bg-red-50"
              aria-label={t('imageUpload.removePhoto')}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={cn(
            'w-32 h-32 flex flex-col items-center justify-center gap-2',
            'border-2 border-dashed rounded-lg',
            'text-gray-500 hover:text-gray-700',
            'hover:border-gray-400 hover:bg-gray-50',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
            isDragging && 'border-primary-500 bg-primary-50',
            'transition-all duration-150 ease-out'
          )}
          aria-describedby={uploadError ? errorId : undefined}
        >
          <ImagePlus className="w-8 h-8" strokeWidth={1.5} />
          <span className="text-xs text-center px-2">{t('imageUpload.uploadPrompt')}</span>
        </button>
      )}

      {uploadError && (
        <p id={errorId} className="text-sm text-red-600" role="alert">
          {uploadError}
        </p>
      )}
    </div>
  )
}
